// src/pages/dashboard/branch/analytics/AnalyticsPage.tsx
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FiPackage,
  FiUsers,
  FiDollarSign,
} from "react-icons/fi";
import { useParams } from "react-router-dom";
import ClientsAnalyticsTab from "./ClientsAnalyticsTab";
import ItemsAnalyticsTab from "./ItemsAnalyticsTab";
import SalesAnalyticsTab from "./SalesAnalyticsTab";
import { AnalyticsProvider, BranchStartDate } from "../../../../context/AnalyticsContext";
import { analyticsService } from "../../../../services/analyticsService";


const AnalyticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"items" | "clients" | "sales">("items");
  const { branchId } = useParams<{ branchId: string }>();
  const { t } = useTranslation();
  const [branchStartDate, setBranchStartDate] = useState<BranchStartDate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch branch start date on component mount
  useEffect(() => {
    const fetchBranchStartDate = async () => {
      if (!branchId) return;

      try {
        setLoading(true);
        setError(null);
        const response = await analyticsService.getBranchStartDate(branchId);
        
        if (response?.success && response.data) {
          setBranchStartDate(response.data);
        } else {
          setError(response?.message || t("analytics.failedToFetchBranchStartDate"));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t("analytics.errorFetchingBranchStartDate"));
        console.error("Error fetching branch start date:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBranchStartDate();
  }, [branchId]);

  const tabs = [
    {
      id: "items" as const,
      label: t("analytics.tabs.items", "Items"),
      icon: FiPackage,
    },
    {
      id: "sales" as const,
      label: t("analytics.tabs.sales", "Sales"),
      icon: FiDollarSign,
    },
    {
      id: "clients" as const,
      label: t("analytics.tabs.clients", "Clients"),
      icon: FiUsers,
    },
  ];

  if (!branchId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">{t("analytics.branchIdNotFound")}</p>
      </div>
    );
  }

  return (
    <AnalyticsProvider
      branchStartDate={branchStartDate}
      loading={loading}
      error={error}
    >
      <div className="px-3 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="space-y-6 sm:space-y-8">
          {/* Header with Tab Navigation */}
          <div className="flex flex-col gap-4">
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                {t("analytics.title")}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                {t("analytics.subtitle")}
              </p>
              {error && (
                <p className="text-red-500 text-sm mt-2">{error}</p>
              )}
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center justify-center w-full">
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl w-full sm:w-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`cursor-pointer flex-1 sm:flex-initial px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === tab.id
                      ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span className="hidden xs:inline sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[500px]">
            {activeTab === "items" && (
              <ItemsAnalyticsTab branchId={branchId} />
            )}
            {activeTab === "sales" && (
              <SalesAnalyticsTab branchId={branchId} />
            )}
            {activeTab === "clients" && (
              <ClientsAnalyticsTab branchId={branchId} />
            )}
          </div>
        </div>
      </div>
    </AnalyticsProvider>
  );
};

export default AnalyticsPage;
